import { useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { ExtendedDbCloudWalletUserInfo } from '@shared/types/db/user';
import { toast } from 'sonner';
import SelectPfp from './SelectPfp';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useUserCloudQuery } from '@/hooks/onboarding/useReferralQuery';
import { useFollowMutation } from '@/hooks/following/useFollowing';
import { fetchReferralCodes } from '@/lib/db/DBReferralHelpers';
import useAppDetails from '@/hooks/utilities/useAppDetails';
const ExperienceLevel = z.nativeEnum({
  New: 'new',
  Medium: 'medium',
  Experienced: 'experienced'
});
type ExperienceLevel = z.infer<typeof ExperienceLevel>;
const formSchema = z.object({
  pfp: z.string().min(1, 'Select an avatar'),
  username: z.string().min(4, 'Username must contain at least 4 characters').max(20, 'Username can not have more than 20 characters').transform(value => !value.startsWith('@') ? `@${value}` : value),
  first_name: z.string().min(1, 'First name cannot be empty'),
  experience: ExperienceLevel
});
const _checkUsernameAvailability = async (username: string) => {
  const _url = `/api/db/checkUsername?username=${username?.trim()}`;
  try {
    const _res = await fetch(_url);
    console.log('_checkUsernameAvailability:res', {
      _res
    });
    if (_res.status === 404) return true;
    if (_res.status === 200) return false;
  } catch (error) {
    console.log('_checkUsernameAvailability:error', {
      error
    });
    throw new Error('error _checkUsernameAvailability');
  }
};
const _updateDbCloudInfo = async (_data: ExtendedDbCloudWalletUserInfo) => {
  const _resp = await fetch('/api/db/setProfile', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(_data)
  });
  const _result = await _resp.json();
  console.log('_updateDbCloudInfo:result', {
    _result
  });
  return {
    status: _resp.status,
    data: _result
  };
};
enum StagesProfileForm {
  default = 'default',
  isCheckingUsername = 'is checking username',
  isUpdatingAccount = 'is creating account',
}
export default function ProfileForm({
  userInfo
}: {
  userInfo: {
    data: ExtendedDbCloudWalletUserInfo;
  };
}) {
  const [stage, setStage] = useState(StagesProfileForm.default);
  const {
    refetch: refetchUserCloudData
  } = useUserCloudQuery(userInfo.data.pkp);
  const followMutation = useFollowMutation();
  const {
    referralUrlCode
  } = useAppDetails();
  const _onSubmitting = useMemo(() => stage === StagesProfileForm.isCheckingUsername || stage === StagesProfileForm.isUpdatingAccount ? true : false, [stage]);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      pfp: '',
      username: '',
      first_name: '',
      // last_name: '',
      experience: 'new'
    }
  });
  const handleOnselectPfp = (_pfp: string) => {
    console.log('selected Pfp', _pfp);
    form.setValue('pfp', _pfp);
  };
  const handleUsernameInputChange = e => {
    const _inputValue = !e.target.value.startsWith('@') ? `@${e.target.value}` : e.target.value;
    form.setValue('username', _inputValue);
  };
  async function onSubmit(values: z.infer<typeof formSchema>) {
    setStage(StagesProfileForm.isCheckingUsername);
    try {
      const _isUsernameAvailable = await _checkUsernameAvailability(values?.username?.trim());
      if (!_isUsernameAvailable) {
        form.setError('username', {
          message: 'Username is not available, try another one.'
        }, {
          shouldFocus: true
        });
        setStage(StagesProfileForm.default);
        return;
      }
      setStage(StagesProfileForm.isUpdatingAccount);
      const _userInfo: ExtendedDbCloudWalletUserInfo = {
        ...userInfo.data,
        username: values.username?.trim(),
        crypto_experience: values.experience,
        pfp: values.pfp,
        first_name: values.first_name
      };
      const _updateUser = await _updateDbCloudInfo(_userInfo);
      console.log('onSubmit:updated profile', {
        _updateUser
      });
      if (_updateUser.status === 201) {
        refetchUserCloudData();
      }
      if (_updateUser.data.error) {
        throw new Error('Error saving your profile');
      }
      try {
        const inputCode = referralUrlCode.toUpperCase();
        const result: any = await fetchReferralCodes({
          referralCode: inputCode
        });
        const followResult = await followMutation.mutateAsync({
          username: values.username?.trim(),
          userToFollow: result?.data?.username
        });
        console.log('followResult', followResult);
      } catch (e) {
        console.log('Failed to follow referral user', e);
      }
    } catch (error) {
      toast.error('Something went wrong, try again', {
        duration: 1000
      });
      setStage(StagesProfileForm.default);
      console.log('onSubmit:ERROR', {
        error
      });
    }
  }
  return <div className='px-6 pt-8 text-genius-cream text-left' data-sentry-component="ProfileForm" data-sentry-source-file="ProfileForm.tsx">
      <Form {...form} data-sentry-element="Form" data-sentry-source-file="ProfileForm.tsx">
        <form onSubmit={form.handleSubmit(onSubmit)} className='grid gap-2 min-h-[96vh] bg '>
          <FormField control={form.control} name='pfp' render={() => <FormItem>
                <FormLabel className='text-[16px] w-full flex justify-center'>
                  Choose Avatar
                </FormLabel>

                <FormControl>
                  <div className='flex flex-col items-center'>
                    <Label htmlFor='pfp' className='cursor-pointer'>
                      <SelectPfp setPfp={handleOnselectPfp} disabled={_onSubmitting} />
                    </Label>
                    <FormMessage />
                  </div>
                </FormControl>
              </FormItem>} data-sentry-element="FormField" data-sentry-source-file="ProfileForm.tsx" />
          <FormField control={form.control} name='username' render={({
          field
        }) => <FormItem>
                <FormLabel className='text-[16px]'>Choose Username</FormLabel>
                <FormControl>
                  <Input {...field} className='border-none bg-genius-blue' disabled={_onSubmitting} placeholder='@SexyEinstein' onChange={handleUsernameInputChange} />
                </FormControl>
                <FormMessage />
              </FormItem>} data-sentry-element="FormField" data-sentry-source-file="ProfileForm.tsx" />
          <div className='grid gap-2 w-full'>
            <FormLabel className='text-[16px]' data-sentry-element="FormLabel" data-sentry-source-file="ProfileForm.tsx">What should we call you?</FormLabel>
            <div className='gap-2 w-full'>
              <FormField control={form.control} name='first_name' render={({
              field
            }) => <FormItem className='w-full'>
                    <FormControl className='w-full'>
                      <Input className='border-none bg-genius-blue w-full' disabled={_onSubmitting} placeholder='Nickname' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} data-sentry-element="FormField" data-sentry-source-file="ProfileForm.tsx" />
            </div>
          </div>
          <FormField control={form.control} name='experience' render={({
          field
        }) => <FormItem className='space-y-2'>
                <FormLabel className='text-[16px]'>How experienced are you with Crypto?</FormLabel>
                <FormControl>
                  <ToggleGroup disabled={Boolean(stage === StagesProfileForm.isCheckingUsername || stage === StagesProfileForm.isUpdatingAccount)} type='single' value={field.value} onValueChange={value => {
              if (value) {
                field.onChange(value);
              }
            }} className='grid grid-cols-1 gap-2'>
                    <ToggleGroupItem value='new' className='flex flex-col items-start h-[62px] p-4 mb-2 border-genius-blue border-[1px] rounded-lg data-[state=on]:bg-genius-blue'>
                      <span className='text-[14px]'>First Timer</span>
                      <span className='text-[12px] ta-l text-genius-cream/50'>
                        I've never traded crypto before, but I'm excited to try
                      </span>
                    </ToggleGroupItem>
                    <ToggleGroupItem value='medium' className='flex flex-col items-start h-[62px] p-4 mb-2 border-genius-blue border-[1px] rounded-lg data-[state=on]:bg-genius-blue'>
                      <span className='text-[14px]'>Been around the block</span>
                      <span className='text-[12px] ta-l text-genius-cream/50'>
                        I own crypto on another platform but I'm no expert
                      </span>
                    </ToggleGroupItem>
                    <ToggleGroupItem value='experienced' className='flex flex-col items-start h-[62px] p-4 border-genius-blue border-[1px] rounded-lg data-[state=on]:bg-genius-blue'>
                      <span className='text-[14px]'>Advanced Degen</span>
                      <span className='text-[12px] ta-l text-genius-cream/50'>
                        I am a crypto God. I am also very humble.
                      </span>
                    </ToggleGroupItem>
                  </ToggleGroup>
                </FormControl>
                <FormMessage />
              </FormItem>} data-sentry-element="FormField" data-sentry-source-file="ProfileForm.tsx" />
          <div className='text-xs text-genius-cream/50 px-2  flex flex-wrap items-center gap-x-1'>
            By creating an account on Genius, you agree to our
            <span onClick={() => window.open('https://www.tradegenius.com/terms-of-use.html', '_blank')} className='text-genius-pink cursor-pointer whitespace-nowrap'>
              Terms of Service
            </span>
            and
            <span onClick={() => window.open('https://www.tradegenius.com/privacy-policy.html', '_blank')} className='text-genius-pink cursor-pointer whitespace-nowrap'>
              Privacy Policy.
            </span>
          </div>

          <Button className=' h-[56px] rounded-full bg-genius-pink md:hover:bg-genius-pink' type='submit' disabled={_onSubmitting} data-sentry-element="Button" data-sentry-source-file="ProfileForm.tsx">
            <div className='flex gap-2'>
              {stage === StagesProfileForm.isCheckingUsername || stage === StagesProfileForm.isUpdatingAccount ? <Loader2 className='h-4 w-4 animate-spin' /> : null}
              {stage === StagesProfileForm.isCheckingUsername && <p>Checking Username</p>}
              {stage === StagesProfileForm.isUpdatingAccount && <p>Saving Profile</p>}
              {stage === StagesProfileForm.default && <p className=''>Continue</p>}
            </div>
          </Button>
        </form>
      </Form>
    </div>;
}